import { upload } from '@vercel/blob/client';
import { aiData, selectedAiPage } from '../../stores/ai-page.js';

/**
 * AiForm
 * @class
 * @constructor
 * @public
 * @version 1.0.0
 */
class AiForm extends HTMLElement {
	constructor() {
		super();

		this.onSubmit = this.onSubmit.bind(this);
	}

	/**
	 * Getters
	 */

	/**
	 * @type HTMLFormElement | null
	 */
	get form() {
		return this.querySelector('form');
	}

	/**
	 * @type HTMLDivElement | null
	 */
	get loader() {
		return this.querySelector('#loader');
	}

	/**
	 * @type HTMLButtonElement | null
	 */
	get submitBtn() {
		return this.querySelector('button[type="submit"]');
	}

	/**
	 * @type HTMLDivElement | null
	 */
	get formInformation() {
		return this.querySelector('#form-information');
	}

	/**
	 * @type HTMLHeadingElement | null
	 */
	get formInformationHeading() {
		return this.querySelector('#form-information-heading');
	}

	/**
	 * @type HTMLDivElement | null
	 */
	get formInformationBody() {
		return this.querySelector('#form-information-body');
	}

	/**
	 * @type HTMLDivElement | null
	 */
	get formResponseDisplay() {
		return this.querySelector('pre');
	}

	/**
	 * @type HTMLDivElement | null
	 */
	get formResponseDisplayCode() {
		return this.querySelector('code');
	}

	/**
	 * connectedCallback
	 * Render html/css & attatch event listeners
	 */
	connectedCallback() {
		this.form?.addEventListener('submit', this.onSubmit);
	}

	/**
	 * disconnectedCallback
	 * Remove listeners.
	 */
	disconnectedCallback() {
		this.openButton?.removeEventListener('click', this.open);
		this.closeButton?.removeEventListener('click', this.close);
		this.openButton?.removeAttribute('aria-controls');
		this.closeButton?.removeAttribute('aria-controls');

		this.restorePopoverAttributes();
	}

	/**
	 * On submit callback
	 * @param {SubmitEvent} event
	 */
	async onSubmit(event) {
		event.preventDefault();
		this.onSubmitBegins();

		const formData = new FormData(this.form);
		const params = new URLSearchParams(formData.get('params'));
		const body = formData.get('body');
		const postData = {};

		if (body) {
			postData['groundingText'] = body;
		}
		const targetAudience =
			parseInt(formData.get('targetAudience'), 10) <= 2
				? 'Beginner'
				: 'Professional';
		const layout =
			parseInt(formData.get('layout'), 10) <= 2 ? 'informative' : 'visual';
		params.set('targetAudience', targetAudience);

		let json;

		const file = formData.get('contentFile');
		let uploadedFile;

		try {
			uploadedFile = await upload(file.name, file, {
				access: 'public',
				handleUploadUrl: '/api/uploads',
			});
		} catch (e) {
			console.error('Couldnt upload pdf to vercel blob');
			console.error(e);
		}

    if (uploadedFile) {
      formData.delete('contentFile');
      formData.set('groundingFileUrl', uploadedFile.url);
    }

		try {
			const response = await fetch(`/api/proxy/ai?${params.toString()}`, {
				// body: JSON.stringify(postData),
				body: formData,
				headers: {
					// "Content-Type": "multipart/form-data",
				},
				method: 'post',
			});
			if (response.status === 200) {
				json = await response.json();
			} else {
				const text = await response.text();
				this.displayError('There was an error', text);
			}
		} catch (error) {
			if (error instanceof SyntaxError) {
				// Unexpected token < in JSON
				this.displayError('There was a SyntaxError', error);
			} else {
				this.displayError('There was an error', error);
			}
		}

		if (json) {
			const id = Date.now();
			// const updatedAiPages = JSON.parse(JSON.stringify(aiPages.get()));
			// json.id = id;
			json.created = id;
			json.menu = json.pages?.map((p) => {
				return { href: `/ai/${p.id}`, text: p.menuName };
			});
			json.pages = json.pages.map((p) => {
				p.content = p.content.map((b) => {
					b.layout = layout;
					return b;
				});
				return p;
			});

			aiData.set(json);
			selectedAiPage.set(1);
			window.location.replace(`/ai/1`);
		}

		this.onSubmitEnds();
	}

	/**
	 * On submit begin callback
	 */
	onSubmitBegins() {
		this.formInformation.classList.add('hidden');
		this.formResponseDisplay?.classList.add('hidden');
		this.loader.classList.remove('hidden');
		this.submitBtn.setAttribute('disabled', '');
	}

	/**
	 * On submit end callback
	 */
	onSubmitEnds() {
		this.loader.classList.add('hidden');
		this.submitBtn.removeAttribute('disabled');
	}

	/**
	 * Display an error
	 */
	displayError(heading, body) {
		this.formInformationHeading.innerText = heading;
		this.formInformationBody.innerText = body;
		this.formInformation.classList.remove('hidden');
	}

	/**
	 * Display a response
	 */
	displayResponse(json) {
		if (this.formResponseDisplayCode) {
			this.formResponseDisplayCode.innerText = json;
			this.formResponseDisplay?.classList.remove('hidden');
		}
	}
}

if (customElements.get('ai-form') === undefined) {
	window.customElements.define('ai-form', AiForm);
}
